/**
 * Results Sidebar Component - GeoSpy-inspired design
 * Right sidebar with coordinates, copy buttons, and action buttons
 */

import { useState } from 'react';
import {
  X,
  MapPin,
  Copy,
  Download,
  Target,
  FolderPlus,
  ChevronDown,
  Crop,
  Maximize2,
  Check,
  ExternalLink,
  AlertTriangle
} from 'lucide-react';

export default function ResultsSidebar({
  isOpen,
  onClose,
  result,
  imageUrl,
  filename,
  onSearchCity,
  onDownloadPDF,
  onStreetView
}) {
  const [copiedField, setCopiedField] = useState(null);
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'nearby'
  const [imageExpanded, setImageExpanded] = useState(false);

  if (!isOpen || !result) return null;

  const copyToClipboard = async (value, field) => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const { coordinates, address, quality, enrichment, serviceStatus } = result;

  // Get confidence from quality object
  const confidence = quality?.confidence || result.confidence || 0;

  // Calculate confidence level colors
  const confidencePercent = Math.round(confidence * 100);
  let confidenceColor = 'text-red-500';
  let confidenceBg = 'bg-red-500';
  let confidenceBorder = 'border-red-500';

  if (confidencePercent > 80) {
    confidenceColor = 'text-primary-500';
    confidenceBg = 'bg-primary-500';
    confidenceBorder = 'border-primary-500';
  } else if (confidencePercent > 50) {
    confidenceColor = 'text-yellow-500';
    confidenceBg = 'bg-yellow-500';
    confidenceBorder = 'border-yellow-500';
  }

  // Helper for amenities bar
  const AmenityBar = ({ label, count, max = 10, icon: Icon }) => {
    const percentage = Math.min((count / max) * 100, 100);
    return (
      <div className="space-y-1.5">
        <div className="flex justify-between text-xs">
          <div className="flex items-center gap-1.5 text-gray-400">
            {Icon && <Icon size={12} />}
            <span>{label}</span>
          </div>
          <span className="text-white font-mono">{count}</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary-600 to-primary-400 rounded-full transition-all duration-1000 ease-out"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Sidebar */}
      <div className="absolute top-4 right-4 bottom-4 w-[480px] bg-[#0a0a0a]/90 backdrop-blur-xl
                    rounded-2xl border border-white/10 overflow-hidden z-30
                    flex flex-col shadow-2xl animate-slide-in-right">

        {/* Header - Glass Effect */}
        <div className="relative px-6 py-5 border-b border-white/5 bg-white/5 backdrop-blur-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-500/10 rounded-lg border border-primary-500/20">
                <MapPin size={18} className="text-primary-500" />
              </div>
              <div>
                <h3 className="font-bold text-white text-lg leading-tight">Intelligence Report</h3>
                <p className="text-xs text-gray-400 font-mono mt-0.5 uppercase tracking-wide">
                  {result.analysisId ? `#${result.analysisId.slice(0, 8)}` : 'UNKNOWN_ID'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-white/10 rounded-full transition-all"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content Scroll Area */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          <div className="p-6 space-y-6">

            {/* Image & Confidence Card */}
            <div className="space-y-4">
              <div className="relative group rounded-xl overflow-hidden border border-white/10 bg-black aspect-video shadow-lg">
                <img
                  src={imageUrl}
                  alt="Analyzed"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />

                {/* Overlay Controls */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2 backdrop-blur-[2px]">
                  <button
                    onClick={() => setImageExpanded(true)}
                    className="p-2.5 bg-white text-black rounded-full hover:scale-110 transition-transform shadow-xl"
                  >
                    <Maximize2 size={18} />
                  </button>
                </div>

                {/* Status Badge */}
                <div className="absolute top-3 left-3 px-2.5 py-1 bg-black/60 backdrop-blur rounded-lg border border-white/10 text-[10px] text-white font-medium uppercase tracking-wider">
                  {filename || 'Uploaded Image'}
                </div>
              </div>

              {/* Confidence Score Bar */}
              <div className="bg-white/5 rounded-xl p-4 border border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400 font-medium">AI Confidence Score</span>
                  <span className={`text-lg font-bold ${confidenceColor}`}>{confidencePercent}%</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${confidenceBg} shadow-[0_0_10px_currentColor] transition-all duration-1000 ease-out`}
                    style={{ width: `${confidencePercent}%` }}
                  />
                </div>
                <div className="mt-2 flex justify-between text-[10px] text-gray-500 font-mono uppercase">
                  <span>Low</span>
                  <span>Medium</span>
                  <span>High</span>
                </div>
              </div>

              {/* Quality Assessment Warnings */}
              {quality?.warnings && quality.warnings.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-yellow-500 mt-0.5 shrink-0" />
                    <div className="flex-1 space-y-2">
                      <p className="text-sm font-semibold text-yellow-200">Quality Warnings</p>
                      {quality.warnings.map((warning, idx) => (
                        <p key={idx} className="text-xs text-yellow-100/90 leading-relaxed">
                          • {warning}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Constraint Warning */}
              {result.constrained_to_portugal && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <Target size={16} className="text-blue-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-blue-200 mb-1">Location Adjusted</p>
                      <p className="text-xs text-blue-100/90 leading-relaxed">
                        Original prediction was outside Portugal ({result.constraint_details?.distance_km}km away).
                        Location adjusted to nearest Portuguese city: <span className="font-semibold text-white">{result.constraint_details?.nearest_city}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Low Confidence Tips */}
              {confidencePercent < 30 && (
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={16} className="text-purple-400 mt-0.5 shrink-0" />
                    <div className="flex-1 space-y-2">
                      <p className="text-sm font-semibold text-purple-200">How to Improve Accuracy</p>
                      <ul className="text-xs text-purple-100/90 space-y-1 leading-relaxed">
                        <li>✓ Upload exterior photos showing the full building</li>
                        <li>✓ Include nearby landmarks or distinctive features</li>
                        <li>✓ Use street-level views with context</li>
                        <li>✗ Avoid interior photos or close-ups</li>
                        <li>✨ Upload 3-5 different angles for better accuracy</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex p-1 bg-white/5 rounded-lg border border-white/5">
              <button
                onClick={() => setActiveTab('details')}
                className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${activeTab === 'details' ? 'bg-white/10 text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'}`}
              >
                Location Details
              </button>
              <button
                onClick={() => setActiveTab('nearby')}
                className={`flex-1 py-2 text-xs font-medium rounded-md transition-all ${activeTab === 'nearby' ? 'bg-white/10 text-white shadow-sm' : 'text-gray-500 hover:text-gray-300'}`}
              >
                Nearby Amenities
              </button>
            </div>

            {activeTab === 'details' ? (
              <div className="space-y-4 animate-fade-in">
                {/* Address Info */}
                <div className="space-y-1">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Detected Location</h4>
                  <p className="text-xl text-white font-medium leading-tight">
                    {address?.city || 'Unknown City'}, {address?.country || 'Unknown Country'}
                  </p>
                  <p className="text-sm text-gray-400">
                    {address?.formatted || 'No specific address detected'}
                  </p>
                </div>

                {/* Coordinates */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white/5 p-3 rounded-xl border border-white/5 hover:border-white/20 transition-colors group">
                    <span className="text-[10px] text-gray-500 uppercase block mb-1">Latitude</span>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-mono text-white">{coordinates?.lat?.toFixed(6)}</span>
                      <button onClick={() => copyToClipboard(coordinates?.lat, 'lat')} className="text-gray-500 hover:text-primary-500 transition-colors">
                        {copiedField === 'lat' ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                  <div className="bg-white/5 p-3 rounded-xl border border-white/5 hover:border-white/20 transition-colors group">
                    <span className="text-[10px] text-gray-500 uppercase block mb-1">Longitude</span>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-mono text-white">{coordinates?.lon?.toFixed(6)}</span>
                      <button onClick={() => copyToClipboard(coordinates?.lon, 'lon')} className="text-gray-500 hover:text-primary-500 transition-colors">
                        {copiedField === 'lon' ? <Check size={14} /> : <Copy size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-5 animate-fade-in">
                {/* Radar Chart Substitute - Progress Bars */}
                <div className="space-y-4">
                  <AmenityBar label="Education & Schools" count={enrichment?.schools || 0} icon={FolderPlus} />
                  <AmenityBar label="Public Transport" count={enrichment?.transport || 0} icon={FolderPlus} />
                  <AmenityBar label="Restaurants & Dining" count={enrichment?.restaurants || 0} icon={FolderPlus} />
                  <AmenityBar label="Markets & Shopping" count={enrichment?.supermarkets || 0} icon={FolderPlus} />
                </div>

                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-start gap-3">
                  <Target size={16} className="text-blue-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-blue-200 leading-relaxed">
                      High density of amenities suggests this is a <span className="font-semibold text-white">Prime Urban Location</span>. Property value likely exceeds regional average.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 bg-white/5 border-t border-white/5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onSearchCity?.(address?.city)}
              className="flex items-center justify-center gap-2 py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white text-sm font-medium transition-all hover:scale-[1.02]"
            >
              <Target size={16} />
              <span>Search Area</span>
            </button>
            <button
              onClick={onDownloadPDF}
              className="flex items-center justify-center gap-2 py-3 px-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-white text-sm font-medium transition-all hover:scale-[1.02]"
            >
              <Download size={16} />
              <span>Report</span>
            </button>
          </div>

          {onStreetView && (
            <button
              onClick={onStreetView}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-4 bg-gradient-to-r from-primary-600 to-primary-500 hover:from-primary-500 hover:to-primary-400 text-white rounded-xl shadow-lg shadow-primary-500/20 font-semibold text-sm transition-all hover:scale-[1.02] hover:shadow-primary-500/40"
            >
              <ExternalLink size={18} />
              Verify with Street View
            </button>
          )}
        </div>

      </div>

      {/* Expanded Image Modal */}
      {imageExpanded && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/95 backdrop-blur-xl animate-fade-in"
          onClick={() => setImageExpanded(false)}
        >
          <button
            onClick={() => setImageExpanded(false)}
            className="absolute top-6 right-6 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
          >
            <X size={24} />
          </button>
          <img
            src={imageUrl}
            alt="Analyzed - Full"
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg shadow-2xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
